import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

interface VerificationFormProps {
  userType: string;
  userId: string;
}

const VerificationForm = ({ userType, userId }: VerificationFormProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    phone: "",
    address: "",
    city: "",
    country: "",
    bio: "",
    businessName: "",
    businessRegistration: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    // Prepare update data with proper field names for edge function
    const updateData: Record<string, string | undefined> = {
      phone: formData.phone,
      address: formData.address,
      city: formData.city,
      country: formData.country,
      bio: formData.bio,
      verification_status: "in_review",
    };

    if (userType === "creator" || userType === "venue") {
      updateData.business_name = formData.businessName;
      updateData.business_registration = formData.businessRegistration;
    }

    try {
      // Use edge function for server-side validation
      const { data, error } = await supabase.functions.invoke('update-profile', {
        body: updateData,
      });

      if (error) throw error;

      if (!data?.success) {
        throw new Error(data?.error || 'Update failed');
      }

      toast({
        title: "Verification submitted!",
        description: "Your information is under review. You'll be notified once approved.",
      });
      navigate("/dashboard");
    } catch (error: any) {
      const errorMessage = error?.details?.join(', ') || error?.message || 'An error occurred';
      toast({
        variant: "destructive",
        title: "Submission failed",
        description: errorMessage,
      });
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-2xl">
        <CardHeader>
          <CardTitle>Complete Your Verification</CardTitle>
          <CardDescription>
            {userType === "groover" && "Please provide additional information to verify your account."}
            {userType === "creator" && "Please provide your creator/artist information for verification."}
            {userType === "venue" && "Please provide your venue business information for verification."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number *</Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="+1 (555) 000-0000"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="country">Country *</Label>
                <Input
                  id="country"
                  type="text"
                  placeholder="United States"
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="address">Address *</Label>
              <Input
                id="address"
                type="text"
                placeholder="123 Main Street"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="city">City *</Label>
              <Input
                id="city"
                type="text"
                placeholder="New York"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                required
              />
            </div>

            {(userType === "creator" || userType === "venue") && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="businessName">
                    {userType === "creator" ? "Artist/Stage Name" : "Venue Name"} *
                  </Label>
                  <Input
                    id="businessName"
                    type="text"
                    placeholder={userType === "creator" ? "Your artist name" : "Your venue name"}
                    value={formData.businessName}
                    onChange={(e) => setFormData({ ...formData, businessName: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="businessRegistration">
                    {userType === "creator" ? "Professional ID" : "Business Registration Number"}
                  </Label>
                  <Input
                    id="businessRegistration"
                    type="text"
                    placeholder={userType === "creator" ? "Optional professional ID" : "Optional registration number"}
                    value={formData.businessRegistration}
                    onChange={(e) => setFormData({ ...formData, businessRegistration: e.target.value })}
                  />
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label htmlFor="bio">Bio</Label>
              <Textarea
                id="bio"
                placeholder="Tell us about yourself..."
                value={formData.bio}
                onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                rows={4}
              />
            </div>

            <div className="flex gap-4">
              <Button type="submit" className="flex-1" disabled={loading}>
                {loading ? "Submitting..." : "Submit for Verification"}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/dashboard")}
              >
                Skip for Now
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default VerificationForm;
